import Vue from 'vue'
import axios from 'axios'
import store from './store'
import router from './router'
import App from './App.vue'

import { authHeader } from './helpers'

import moment from 'moment'
import 'moment/locale/es'

import numeral from 'numeral'

import vueDebounce from 'vue-debounce'

import Snotify, { SnotifyPosition } from 'vue-snotify'
import 'vue-snotify/styles/material.css'

import * as VueGoogleMaps from 'vue2-google-maps'

import VueTheMask from 'vue-the-mask'

import Money from 'v-money'

import vmodal from 'vue-js-modal'
import 'vue-js-modal/dist/styles.css'

import ToggleButton from 'vue-js-toggle-button'

import VueUploadComponent from 'vue-upload-component'

import VueSweetalert2 from 'vue-sweetalert2'
import 'sweetalert2/dist/sweetalert2.min.css'
import Swal from 'sweetalert2'

import VTooltip from 'v-tooltip'

require('bootstrap')
import './assets/custom.css'

// ACL permisos usuario
require('./helpers/acl')

// Moment configuración
moment.locale('es')
Vue.prototype.moment = moment

// Header auth inicial
authHeader()

// Plugins
Vue.use(vueDebounce)

Vue.use(Snotify, {
  toast: {
    position: SnotifyPosition.rightTop,
    timeout: 6000
  }
})

Vue.use(VueGoogleMaps, {
  load: {
    key: 'AIzaSyCNh1zGzQyZNuVHCc2uFwdKOLqDKDgiI4g',
    libraries: 'places',
    region: 'MX',
    language: 'es-419',
  },
  autobindAllEvents: false,
  installComponents: true
})

Vue.use(VueTheMask)

Vue.use(Money, {
  decimal: '.',
  thousands: ',',
  prefix: '$ ',
  suffix: '',
  precision: 2,
  masked: true
})

Vue.use(vmodal)

Vue.use(ToggleButton)

Vue.component('file-upload', VueUploadComponent)

Vue.use(VueSweetalert2)

Vue.use(VTooltip)

// ======================================================
// Sesión / Token JWT
// ======================================================

let sessionExpiredHandled = false
let sessionWarningTimer = null
let sessionExpireTimer = null
let sessionWarningOpen = false

const decodeJwt = (token) => {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(window.atob(base64))
  } catch (error) {
    return null
  }
}

const clearSessionTimers = () => {
  if (sessionWarningTimer) {
    clearTimeout(sessionWarningTimer)
    sessionWarningTimer = null
  }

  if (sessionExpireTimer) {
    clearTimeout(sessionExpireTimer)
    sessionExpireTimer = null
  }
}

const cleanFrontendSession = () => {
  store.commit('auth/setUser', null)
  store.commit('auth/setToken', null)

  delete axios.defaults.headers.common['Authorization']
}

const redirectToLoginExpired = () => {
  if (router.currentRoute.name !== 'login') {
    router.replace({
      name: 'login',
      query: {
        expired: '1'
      }
    }).catch(() => {})
  }
}

const closeSessionExpired = () => {
  clearSessionTimers()
  cleanFrontendSession()
  redirectToLoginExpired()
}

const showSessionWarning = async () => {
  if (sessionWarningOpen) {
    return
  }

  const token = localStorage.getItem('access_token')

  if (!token) {
    closeSessionExpired()
    return
  }

  const payload = decodeJwt(token)

  if (!payload || !payload.exp) {
    closeSessionExpired()
    return
  }

  const expiresAt = payload.exp * 1000
  const now = Date.now()
  const expireTime = expiresAt - now

  if (expireTime <= 0) {
    closeSessionExpired()
    return
  }

  const minutesLeft = Math.max(1, Math.ceil(expireTime / 60000))

  sessionWarningOpen = true

  const result = await Swal.fire({
    title: 'Tu sesión está por expirar',
    text: `Por seguridad, tu sesión se cerrará en ${minutesLeft} minuto(s). ¿Deseas continuar trabajando?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Continuar sesión',
    cancelButtonText: 'Cerrar sesión',
    allowOutsideClick: false,
    allowEscapeKey: false,
    timer: expireTime,
    timerProgressBar: true
  })

  sessionWarningOpen = false

  if (result.isConfirmed) {
    try {
      const renewed = await store.dispatch('auth/refreshToken')

      if (renewed) {
        sessionExpiredHandled = false

        Swal.fire({
          title: 'Sesión renovada',
          text: 'Puedes continuar trabajando.',
          icon: 'success',
          timer: 1800,
          showConfirmButton: false
        })

        startSessionWatcher()
        return
      }
    } catch (error) {
      console.log('No se pudo renovar la sesión.', error)
    }
  }

  await store.dispatch('auth/logout')
  closeSessionExpired()
}

const startSessionWatcher = () => {
  clearSessionTimers()

  const token = localStorage.getItem('access_token')

  if (!token) {
    return
  }

  const payload = decodeJwt(token)

  if (!payload || !payload.exp) {
    return
  }

  const expiresAt = payload.exp * 1000
  const now = Date.now()
  const expireTime = expiresAt - now

  if (expireTime <= 0) {
    closeSessionExpired()
    return
  }

  /**
   * Para pruebas:
   * Si el token dura 5 o 6 minutos, avisamos 1 minuto antes.
   *
   * Para producción:
   * Si el token dura más, avisamos 5 minutos antes.
   */
  const warningBefore = expireTime <= (6 * 60 * 1000)
    ? 1 * 60 * 1000
    : 5 * 60 * 1000

  const warningTime = expireTime - warningBefore

  /**
   * Si recargas la página y el token ya está dentro del rango de aviso,
   * el modal sale inmediatamente.
   */
  if (warningTime <= 0) {
    showSessionWarning()
  } else {
    sessionWarningTimer = setTimeout(() => {
      showSessionWarning()
    }, warningTime)
  }

  sessionExpireTimer = setTimeout(() => {
    closeSessionExpired()
  }, expireTime)
}

// ======================================================
// Interceptor global para token vencido
// ======================================================

axios.interceptors.response.use(
  response => response,
  error => {
    const status = error.response ? error.response.status : null
    const originalRequest = error.config || {}

    const isAuthRequest =
      originalRequest.url &&
      (
        originalRequest.url.includes('/auth/login') ||
        originalRequest.url.includes('/auth/logout') ||
        originalRequest.url.includes('/auth/refresh') ||
        originalRequest.url.includes('/auth/validation-user')
      )

    if (
      (status === 401 || status === 403 || status === 419) &&
      !sessionExpiredHandled &&
      !isAuthRequest
    ) {
      sessionExpiredHandled = true
      closeSessionExpired()
    }

    return Promise.reject(error)
  }
)

// Filtros
Vue.filter('toCurrency', function (value) {
  if (value) {
    return '$ ' + Number(parseFloat(value).toFixed(2)).toLocaleString('en', {
      minimumFractionDigits: 2
    })
  } else if (Math.sign(value)) {
    return '$ ' + Number(parseFloat(value).toFixed(2)).toLocaleString('en', {
      minimumFractionDigits: 2
    })
  } else {
    return '$ 0.00'
  }
})

Vue.filter('toDateTime', function (value) {
  if (value) {
    return moment(value).format('DD/MM/YYYY')
  } else {
    return 'Sin Fecha'
  }
})

Vue.filter('toDateTimeHour', function (value) {
  if (value) {
    return moment(value).format('DD/MM/YYYY HH:mm:ss')
  } else {
    return 'Sin Fecha'
  }
})

Vue.filter('money', function (value) {
  if (value) {
    return numeral(value).format('$0,0.00')
  } else {
    return parseFloat(0)
  }
})

Vue.config.productionTip = false

Vue.mixin({
  data: function () {
    return {
      get globalUrlFiles() {
        return window.location.hostname === 'localhost'
          ? 'http://127.0.0.1:8000'
          : (
              window.location.hostname === 'grupom.front.localhost'
                ? 'http://grupom.backend.localhost'
                : (
                    window.location.hostname === 'dev.grupomcloud.com'
                      ? 'https://dev.api.grupomcloud.com'
                      : 'https://api.grupomcloud.com'
                  )
            )
      }
    }
  }
})

window.Event = new Vue()

startSessionWatcher()

new Vue({
  el: '#app',
  router,
  store,
  render: h => h(App)
})